import { USER_KEY, SELECTED_CHAIN_KEY, DEFAULT_API_ADMIN } from '../constants';
import * as authManager from '../lib/authManager';
import { track, EVENTS, identifyUser, clearIdentity } from '../lib/analytics';

/**
 * A sessão do admin é um par de chaves que vive no IndexedDB deste navegador; o access
 * token fica só em memória e é renovado assinando uma prova com a chave privada
 * (ver lib/authManager.ts). O localStorage guarda apenas o perfil para a UI.
 *
 * Login tem duas etapas: a senha devolve um `loginToken` curto, e o cadastro do
 * dispositivo (authManager.enroll) é que abre a sessão.
 */

const sendJson = async (method, path, body, authorization) => {
    const headers = { 'Content-Type': 'application/json' };
    if (authorization) { headers.Authorization = authorization; }

    const response = await fetch(`${DEFAULT_API_ADMIN}${path}`, {
        method,
        body: JSON.stringify(body),
        headers: new Headers(headers),
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        const error = new Error(payload.message || response.statusText);
        error.status = response.status;
        throw error;
    }

    return payload;
};

const clearProfile = () => {
    localStorage.removeItem(USER_KEY);
    // Sem isto o próximo login neste navegador herda a rede escolhida pelo anterior.
    localStorage.removeItem(SELECTED_CHAIN_KEY);
    localStorage.removeItem('fullName');
    localStorage.removeItem('role');
    localStorage.removeItem('authorization');
    localStorage.removeItem('id');
    localStorage.removeItem('permissions');
    // Resíduo do desenho antigo, quando o JWT morava aqui.
    localStorage.removeItem('jwt-token');
};

const authProvider = {
    login: async params => {
        const { username, password } = params;

        let user;
        try {
            user = await sendJson('POST', '/employees/login', { login: username?.trim(), password });
        } catch (error) {
            track(EVENTS.LOGIN_FAILED, { status: error.status });
            throw error;
        }

        // Senha obrigatória: a tela de login assume o fluxo e chama updatePassword,
        // que é quem cadastra o dispositivo depois.
        if (user?.mustChangePassword === true) {
            throw user;
        }

        await authManager.enroll(user.loginToken);
        localStorage.setItem('id', user.id);

        identifyUser(user);
        track(EVENTS.LOGIN_SUCCESS, { role: user?.role ?? 'unknown' });
    },
    updatePassword: async (id, params, user) => {
        const { password } = params;
        await sendJson('PATCH', `/employees/${id}/update-password`, { password: password?.trim() }, user.loginToken);

        // A troca de senha revoga as sessões no backend, então o dispositivo é
        // cadastrado depois dela — nunca antes.
        await authManager.enroll(user.loginToken);
        localStorage.setItem('id', user.id);
    },
    logout: async () => {
        await authManager.logout();
        clearProfile();
        track(EVENTS.LOGOUT);
        clearIdentity();
    },
    checkAuth: async () => {
        // Nada é carregado do storage: o token é forjado agora com a chave do dispositivo.
        if (await authManager.ensure()) { return; }
        throw { redirectTo: '/login' };
    },
    checkError: async error => {
        const { status } = error;

        // Só 401 derruba sessão. O Hapi devolve 403 para escopo insuficiente, e tratar
        // isso como fim de sessão deslogava quem apenas abriu uma tela sem permissão.
        if (status === 401) {
            await authManager.logout();
            clearProfile();
            track(EVENTS.AUTH_LOGOUT_FORCED, { status });
            clearIdentity();
            throw new Error('logout');
        }
    },
    getPermissions: async () => {
        await authManager.ensure();

        const { permissions } = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
        const effective = permissions?.length ? permissions : authManager.getScope();

        if (!effective.length) { throw new Error('logout'); }

        return effective;
    },
    getIdentity: () => {
        const { id, employeeName } = JSON.parse(localStorage.getItem(USER_KEY) || '{}');
        return Promise.resolve({ id, fullName: employeeName });
    }
};
export default authProvider;
